import styled from 'styled-components';
import { theme } from '../../global-styles';

export const ReportAbuseWrapper = styled.div<{ lang: string }>`
  min-height: 100vh;
  background: ${theme.colours.white};
  direction: ${(props) => (props.lang === 'ar' ? 'rtl' : 'ltr')};
`;

export const ReportHeader = styled.div<{ lang?: string }>`
  background: ${theme.colours.white};
  padding: 40px 32px;
  border-bottom: 1px solid ${theme.colours.Platinum};
  text-align: ${(props) => (props.lang === 'ar' ? 'right' : 'left')};

  .header-content {
    max-width: 1200px;
    margin: 0 auto;
  }

  .logo {
    width: 100px;
    height: auto;
    margin-bottom: 24px;
  }

  h1 {
    font-family: ${theme.fonts.SemiBold};
    font-size: 32px;
    color: ${theme.colours.black};
    margin-bottom: 16px;
    line-height: 1.3;
  }

  .description {
    font-family: ${theme.fonts.Regular};
    font-size: 16px;
    color: ${theme.colours.gray2};
    line-height: 1.6;
    max-width: 700px;
  }

  @media (max-width: ${theme.brekpoints.tablet}) {
    padding: 24px 20px;

    h1 {
      font-size: 24px;
    }

    .description {
      font-size: 14px;
    }

    .logo {
      width: 80px;
    }
  }
`;

export const BackButton = styled.button<{ lang: string }>`
  background: transparent;
  border: none;
  color: ${theme.colours.NewCar};
  font-family: ${theme.fonts.Medium};
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  padding: 8px 0;
  transition: opacity 0.2s ease;

  .arrow {
    font-size: 18px;
    transform: ${(props) => (props.lang === 'ar' ? 'scaleX(-1)' : 'none')};
  }

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: ${theme.brekpoints.tablet}) {
    font-size: 14px;
    .arrow {
      font-size: 16px;
    }
  }
`;

export const ReportContent = styled.div<{ lang?: string }>`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 0px;
  text-align: ${(props) => (props.lang === 'ar' ? 'right' : 'left')};

  .info-notice {
    background: transparent;
    border: 1px solid ${theme.colours.black};
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 40px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    max-width: calc(100% - 420px);

    @media (max-width: 1024px) {
      max-width: 100%;
    }

    .info-icon {
      font-size: 20px;
      margin-top: 2px;
      flex-shrink: 0;
      color: ${theme.colours.black};
    }

    p {
      font-family: ${theme.fonts.Regular};
      font-size: 14px;
      color: ${theme.colours.black};
      line-height: 1.5;
      margin: 0;

      a {
        color: ${theme.colours.NewCar};
        text-decoration: underline;
      }
    }
  }

  .main-content {
    align-items: flex-start;

    @media (max-width: 1024px) {
      flex-direction: column;
      gap: 32px;
    }
  }

  .form-section {
    flex: 1;
    min-width: 0;
    background: ${theme.colours.white};
    border-radius: 12px;
    padding: 32px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    width: 100%;
  }

  .form-group {
    margin-bottom: 32px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .form-label {
    display: block;
    font-family: ${theme.fonts.Medium};
    font-size: 16px;
    color: ${theme.colours.black};
    margin-bottom: 16px;
  }

  .radio-group {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .radio-option {
    .controleRadio {
      align-items: flex-start;

      label {
        .textValue {
          font-size: 16px;
          color: ${theme.colours.black};
          line-height: 1.5;
        }
      }
    }
  }

  .textarea {
    width: 100%;
    min-height: 120px;
    padding: 16px;
    border: 2px solid ${theme.colours.Platinum};
    border-radius: 8px;
    font-family: ${theme.fonts.Regular};
    font-size: 14px;
    resize: vertical;
    transition: border-color 0.2s ease;

    &:focus {
      border-color: ${theme.colours.NewCar};
      outline: none;
      box-shadow: 0 0 0 3px rgba(12, 88, 219, 0.1);
    }

    &::placeholder {
      color: ${theme.colours.gray3};
    }
  }

  @media (max-width: ${theme.brekpoints.desketop}) {
    padding: 40px 20px;
  }

  @media (max-width: ${theme.brekpoints.tablet}) {
    .form-section {
      padding: 24px;
    }

    .form-label {
      font-size: 14px;
    }
  }
`;

export const InfoSection = styled.div<{ lang?: string }>`
  width: 380px;
  flex-shrink: 0;
  text-align: ${(props) => (props.lang === 'ar' ? 'right' : 'left')};

  /* Desktop version - show on right side */
  &.desktop-info {
    @media (max-width: 1024px) {
      display: none;
    }
  }

  /* Mobile version - show above form */
  &.mobile-info {
    display: none;
    order: -1;
    
    @media (max-width: 1024px) {
      display: block;
      width: 100%;
      margin-bottom: 32px;
    }
  }

  .info-card {
    background: ${theme.colours.AntiFlashWhite};
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    h4 {
      font-family: ${theme.fonts.Medium};
      font-size: 16px;
      color: ${theme.colours.black};
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      gap: 8px;

      .info-icon {
        font-size: 20px;
        color: ${theme.colours.black};
      }
    }

    .info-content {
      p {
        font-family: ${theme.fonts.Regular};
        font-size: 14px;
        color: ${theme.colours.black};
        line-height: 1.6;
        margin-bottom: 16px;

        &:last-child {
          margin-bottom: 0;
        }

        strong {
          font-family: ${theme.fonts.Medium};
          color: ${theme.colours.black};
        }
      }
    }
  }

  @media (max-width: 1024px) {
    width: 100%;
  }
`;

export const SubmitButton = styled.button`
  background: ${theme.colours.NewCar};
  color: ${theme.colours.white};
  border: none;
  border-radius: 8px;
  padding: 16px 32px;
  font-family: ${theme.fonts.Medium};
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 160px;
  margin-top: 24px;

  &:hover:not(:disabled) {
    background: #0a4bc4;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(12, 88, 219, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: ${theme.colours.gray3};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: ${theme.brekpoints.tablet}) {
    width: 100%;
    padding: 18px 32px;
  }
`;
